import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Mail, MapPin, Phone, Building2, Send, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/product" className="text-slate-600 hover:text-slate-900">Product</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
            <Link to="/about" className="text-slate-600 hover:text-slate-900">About</Link>
            <Link to="/contact" className="text-emerald-700 font-medium">Contact</Link>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login"><Button variant="ghost">Log in</Button></Link>
            <Link to="/signup"><Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full">Get Started</Button></Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </nav>
  );
};

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name, email, company, message });
      setSubmitted(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Navbar />
      
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-6" data-testid="contact-title">
                Get in Touch
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-12">
                Have questions about {process.env.REACT_APP_APP_NAME || 'Flowteras'}? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-email-block">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Email</h3>
                    <a
                      href="mailto:support@flowteras.com"
                      className="text-slate-600 dark:text-slate-400 hover:text-emerald-700 dark:hover:text-emerald-400"
                      data-testid="contact-support-email"
                    >
                      support@flowteras.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-office-block">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Office</h3>
                    <p className="font-medium text-slate-900 dark:text-white mt-1" data-testid="contact-company-name">
                      GREENMOUNT SOLUTIONS SDN. BHD.
                    </p>
                    <p className="text-slate-600 dark:text-slate-400 mt-1" data-testid="contact-office-address">
                      D-07-13A, BSP 21<br />
                      Persiaran Saujana Utama<br />
                      42610 Jenjarom<br />
                      Selangor<br />
                      Malaysia
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone-block">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Phone</h3>
                    <a
                      href="tel:+60176025195"
                      className="text-slate-600 dark:text-slate-400 hover:text-emerald-700 dark:hover:text-emerald-400"
                      data-testid="contact-phone-number"
                    >
                      +60176025195
                    </a>
                  </div>
                </div>
              </div>

              <div
                className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-6"
                data-testid="contact-business-ownership"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Business Ownership</h3>
                    <p className="text-slate-600 dark:text-slate-400 mt-1">
                      Flowteras is developed, owned and operated by{' '}
                      <span className="font-medium text-slate-900 dark:text-white">
                        GREENMOUNT SOLUTIONS SDN. BHD.
                      </span>
                    </p>
                    <p className="text-slate-600 dark:text-slate-400 mt-3">
                      <span className="font-medium text-slate-900 dark:text-white">Registration No:</span>{' '}
                      <span data-testid="contact-registration-number">202401025006 (1570855-W)</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl p-8" data-testid="contact-form">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Message Sent!</h2>
                  <p className="text-slate-600 dark:text-slate-400">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company">Company (Optional)</Label>
                    <Input
                      id="company"
                      placeholder="Your Company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      data-testid="contact-company"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us how we can help..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={5}
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-700 hover:bg-emerald-800"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
